/**
 * One-time backup-code regeneration console.
 *
 * Route: /platform/tools/regen-backup-codes  (Platform Admin only)
 *
 * Behaviour contract:
 *   - Codes are rendered ONCE from the API response into transient React
 *     state. They are never written to localStorage, sessionStorage,
 *     cookies, IndexedDB, console, or any DOM attribute other than the
 *     visible <code> nodes.
 *   - Refreshing / closing the tab wipes them from memory.
 *   - Every render of this page also fires a `history.replaceState` on
 *     mount so the codes never appear in the browser back/forward cache.
 *   - The "Clear from screen" button hard-nulls the codes and drops
 *     input focus.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api, { toErrMsg } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ShieldAlert, EyeOff, Copy, KeyRound, AlertTriangle } from "lucide-react";

export default function RegenBackupCodes() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [codes, setCodes] = useState(null);         // null | string[]
  const [issuedFor, setIssuedFor] = useState("");
  const [confirmArmed, setConfirmArmed] = useState(false);

  // Kick to /login if unauthenticated (the api interceptor also does this).
  // Show a hard "not allowed" if signed in but not Platform Admin.
  const role = (user?.role || "").trim();
  const allowed = role === "Platform Admin" || role === "Partner";

  useEffect(() => {
    // Wipe any router state that might have leaked in.
    window.history.replaceState({}, "");
    // Belt-and-braces: keep the codes out of the bfcache when the user
    // navigates back to this tab from another site.
    const onPageShow = (e) => {
      if (e.persisted) setCodes(null);
    };
    window.addEventListener("pageshow", onPageShow);
    return () => {
      // On unmount, drop everything.
      setCodes(null);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, []);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!email.trim()) {
      toast.error("Enter the user's email");
      return;
    }
    if (!confirmArmed) {
      toast.error("Tick the confirmation checkbox first");
      return;
    }
    setBusy(true);
    setCodes(null);
    try {
      const r = await api.post(
        "/admin/twofa/regen-backup-codes",
        { user_email: email.trim() },
        { _silent: true, headers: { "Cache-Control": "no-store" } },
      );
      setCodes(r.data.codes || []);
      setIssuedFor(r.data.email || email.trim());
      setConfirmArmed(false);
      toast.success(`Fresh backup codes issued for ${r.data.email}`);
    } catch (err) {
      toast.error(toErrMsg(err, "Regeneration failed"));
    } finally {
      setBusy(false);
    }
  };

  const clearFromScreen = () => {
    setCodes(null);
    setIssuedFor("");
    setEmail("");
    // Force focus off any element that might have been mid-copy.
    if (document.activeElement?.blur) document.activeElement.blur();
  };

  const copyAll = async () => {
    if (!codes?.length) return;
    try {
      await navigator.clipboard.writeText(codes.join("\n"));
      toast.success("Copied — paste into a secure channel now");
    } catch {
      toast.error("Clipboard unavailable — copy manually");
    }
  };

  const grouped = useMemo(() => codes || [], [codes]);

  if (!user) {
    return (
      <div className="min-h-screen grid place-items-center p-6 text-sm text-slate-600">
        Loading session…
      </div>
    );
  }

  if (!allowed) {
    return (
      <div className="min-h-screen grid place-items-center p-6">
        <div className="max-w-md rounded-lg border border-red-200 bg-red-50 p-6 text-sm text-red-900" data-testid="regen-forbidden">
          <div className="flex items-center gap-2 font-semibold text-red-800 mb-1">
            <ShieldAlert className="h-4 w-4" /> Not authorised
          </div>
          You must be signed in as a <b>Platform Admin</b> or <b>Partner</b>
          {" "}to reissue backup codes.
          <div className="mt-3">
            <Button variant="outline" size="sm" onClick={() => nav("/")}>
              Return to app
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="regen-backup-page">
      <div className="mx-auto max-w-2xl">
        <div className="mb-4 flex items-center gap-2">
          <KeyRound className="h-5 w-5 text-slate-700" />
          <h1 className="text-xl font-semibold text-slate-800">
            Reissue Backup Codes
          </h1>
        </div>
        <p className="mb-4 text-sm text-slate-600">
          Regenerates ten fresh single-use backup codes for the target user.
          The old codes are invalidated the moment new ones are issued. This
          page shows the codes <b>once</b> — refresh, navigate away, or click
          "Clear from screen" and they are gone.
        </p>

        <form
          onSubmit={submit}
          className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm"
        >
          <label className="block text-sm font-medium text-slate-700 mb-1">
            User email
          </label>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="user@example.com"
            autoComplete="off"
            spellCheck={false}
            disabled={busy}
            data-testid="regen-email"
            className="mb-3"
          />

          <label className="flex items-start gap-2 text-xs text-amber-900 mb-3">
            <input
              type="checkbox"
              checked={confirmArmed}
              onChange={(e) => setConfirmArmed(e.target.checked)}
              className="mt-0.5"
              data-testid="regen-confirm"
            />
            <span>
              I understand this <b>invalidates all previous backup codes</b>
              {" "}for this user and writes an audit-trail row
              {" "}(<code>kind=admin_regen_backup_codes</code>).
            </span>
          </label>

          <Button
            type="submit"
            disabled={busy || !confirmArmed || !email.trim()}
            data-testid="regen-submit"
          >
            {busy ? "Issuing…" : "Reissue 10 fresh codes"}
          </Button>
        </form>

        {codes && (
          <div
            className="mt-6 rounded-lg border-2 border-amber-400 bg-amber-50 p-4"
            data-testid="regen-codes-card"
            aria-live="polite"
          >
            <div className="flex items-center gap-2 text-sm font-semibold text-amber-900 mb-2">
              <AlertTriangle className="h-4 w-4" />
              Shown only now — save or hand off, then click "Clear from screen".
            </div>
            <div className="mb-2 text-xs text-amber-900">
              Issued for <b>{issuedFor}</b>. Each code works exactly once.
              Not stored in browser cache, localStorage, cookies, or logs.
            </div>

            <ul
              className="grid grid-cols-2 gap-2 my-3"
              data-testid="regen-codes-list"
            >
              {grouped.map((c, i) => (
                <li
                  key={i}
                  className="rounded border border-amber-300 bg-white px-3 py-2 font-mono text-sm tracking-wider text-slate-900 text-center select-all"
                  data-testid={`regen-code-${i}`}
                >
                  {c}
                </li>
              ))}
            </ul>

            <div className="mt-3 flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={copyAll}
                data-testid="regen-copy-all"
              >
                <Copy className="h-4 w-4 me-1" /> Copy all
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={clearFromScreen}
                data-testid="regen-clear"
              >
                <EyeOff className="h-4 w-4 me-1" /> Clear from screen
              </Button>
            </div>
          </div>
        )}

        <div className="mt-6 text-xs text-slate-500">
          Signed in as <b>{user.email}</b> ({role}). All reissues are logged
          in <code>audit_trails</code>.
        </div>
      </div>
    </div>
  );
}
